import { type FormEvent, useEffect, useState } from "react";
import {
  PageContainer,
  PageContent,
  PageHeader,
} from "../components/layout/PageHeader";
import { Alert } from "../components/ui/Alert";
import { Button } from "../components/ui/Button";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Spinner } from "../components/ui/Spinner";
import { useProfile, useUpdateProfile } from "../hooks/api/useProfile";
import { useToastStore } from "../stores/toastStore";

function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
}

export function ProfilePage() {
  const { data, isLoading, error } = useProfile();
  const updateProfile = useUpdateProfile();
  const addToast = useToastStore((s) => s.addToast);
  const [name, setName] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (data?.user) {
      setName(data.user.name);
    }
  }, [data]);

  const isUnchanged = data?.user.name === name.trim();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);

    try {
      await updateProfile.mutateAsync({ name: name.trim() });
      addToast("表示名を変更しました");
    } catch (err) {
      setFormError(getErrorMessage(err, "表示名の変更に失敗しました"));
    }
  };

  return (
    <PageContainer>
      <PageHeader />
      <PageContent>
        <div className="max-w-lg mx-auto">
          <Card>
            <CardHeader title="プロフィール" />
            <CardContent>
              {isLoading && (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              )}
              {error && (
                <Alert variant="error">
                  プロフィールの読み込みに失敗しました
                </Alert>
              )}
              {!(isLoading || error) && data && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {formError && <Alert variant="error">{formError}</Alert>}
                  <Input
                    label="メールアドレス"
                    value={data.user.email}
                    disabled
                  />
                  <Input
                    label="表示名"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={256}
                  />
                  <Button
                    type="submit"
                    isLoading={updateProfile.isPending}
                    disabled={isUnchanged || updateProfile.isPending}
                  >
                    保存
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </PageContent>
    </PageContainer>
  );
}
